import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Clarification {
  id?: string
  question: string
  answer: string
  order: number
}

interface ClarificationsSectionProps {
  clarifications: Clarification[]
  onUpdate: (clarifications: Clarification[]) => void
}

export function ClarificationsSection({
  clarifications,
  onUpdate,
}: ClarificationsSectionProps) {
  const handleAddClarification = () => {
    const newClarification: Clarification = {
      question: '',
      answer: '',
      order: clarifications.length,
    }
    onUpdate([...clarifications, newClarification])
  }

  const handleUpdateClarification = (
    index: number,
    field: 'question' | 'answer',
    value: string,
  ) => {
    const updated = [...clarifications]
    updated[index] = { ...updated[index], [field]: value }
    onUpdate(updated)
  }

  const handleRemoveClarification = (index: number) => {
    const updated = clarifications.filter((_, i) => i !== index)
    // Re-order remaining clarifications
    onUpdate(updated.map((c, i) => ({ ...c, order: i })))
  }

  const handleMoveClarification = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === clarifications.length - 1)
    ) {
      return
    }

    const updated = [...clarifications]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate(updated.map((c, i) => ({ ...c, order: i })))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button type="button" onClick={handleAddClarification} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Clarification
        </Button>
      </div>

      {clarifications.length === 0 ? (
        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <p className="text-muted-foreground mb-4">
            No clarifications added yet.
          </p>
          <Button
            type="button"
            onClick={handleAddClarification}
            variant="outline"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add First Clarification
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {clarifications.map((clarification, index) => (
            <div
              key={clarification.id || `clarification-${index}`}
              className="border-border bg-muted/30 rounded-lg border p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="text-muted-foreground h-4 w-4" />
                  <span className="text-sm font-medium">
                    Clarification {index + 1}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveClarification(index, 'up')}
                    disabled={index === 0}
                    className="h-7 w-7 p-0"
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveClarification(index, 'down')}
                    disabled={index === clarifications.length - 1}
                    className="h-7 w-7 p-0"
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveClarification(index)}
                    className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label
                    htmlFor={`question-${index}`}
                    className="text-muted-foreground text-xs font-medium"
                  >
                    Question
                  </label>
                  <textarea
                    id={`question-${index}`}
                    value={clarification.question}
                    onChange={(e) =>
                      handleUpdateClarification(
                        index,
                        'question',
                        e.target.value,
                      )
                    }
                    placeholder="Can the array contain duplicate values?"
                    className="border-input bg-background mt-1 min-h-[60px] w-full resize-y rounded-md border px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`answer-${index}`}
                    className="text-muted-foreground text-xs font-medium"
                  >
                    Answer
                  </label>
                  <textarea
                    id={`answer-${index}`}
                    value={clarification.answer}
                    onChange={(e) =>
                      handleUpdateClarification(index, 'answer', e.target.value)
                    }
                    placeholder="Yes, the array may contain duplicate values."
                    className="border-input bg-background mt-1 min-h-[60px] w-full resize-y rounded-md border px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
